import type { ReactNode } from 'react';

/**
 * Badge — small inline label. Used for link-metadata callouts on the
 * Overview ("📄 Lander", "🛡️ Shield", "🎯 3 variations"), "You" on the
 * Members page, "Soon" on the Settings tiles, etc.
 *
 * Tones use DESIGN.md "container" tokens so badges feel like part of the
 * design system instead of generic chips.
 *
 * Use Badge for STATIC metadata. For status-like states (active /
 * pending / failed), use StatusPill instead — it has the colored dot
 * affordance that signals "this is a state, not a label."
 */

type Tone =
  | 'primary'      // violet — default / branded labels
  | 'secondary'    // green — "active feature", upgrade hints
  | 'tertiary'     // peach — "new", "preview"
  | 'error'        // red — destructive metadata
  | 'neutral';     // white-glass — generic / "Soon"

const TONE: Record<Tone, { bg: string; text: string }> = {
  primary: { bg: 'rgba(124, 77, 255, 0.18)', text: '#cdbdff' },
  secondary: { bg: 'rgba(125, 255, 162, 0.16)', text: '#7dffa2' },
  tertiary: { bg: 'rgba(255, 182, 136, 0.16)', text: '#ffb688' },
  error: { bg: 'rgba(255, 180, 171, 0.14)', text: '#ffb4ab' },
  neutral: { bg: 'rgba(255, 255, 255, 0.08)', text: '#cac3d8' },
};

export function Badge({
  tone = 'neutral',
  children,
  title,
  className = '',
}: {
  tone?: Tone;
  children: ReactNode;
  title?: string;
  className?: string;
}) {
  const t = TONE[tone];
  return (
    <span
      title={title}
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.06em] ${className}`}
      style={{ background: t.bg, color: t.text }}
    >
      {children}
    </span>
  );
}
